import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { loginUser, googleSignIn, googleSignUp, facebookLogin, getUserById, logoutUser } from '../apis/authApi';
import { useError } from './ErrorContext';
import { getLoginRedirectPath } from '../utils/authUtils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize auth state from localStorage if available
  const initialState = {
    isAuthenticated: false,
    token: localStorage.getItem('authToken'),
    user: JSON.parse(localStorage.getItem('authUser')) || null,
  };

  const [authState, setAuthState] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [zombieUser, setZombieUser] = useState(null);

  // Get error context for global error handling
  const { addError, handleApiCall } = useError();

  // Helper function to clear auth state and redirect
  const clearAuthState = () => {
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null
    });
    setZombieUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('userState');
    localStorage.removeItem('cardState');
    localStorage.removeItem('cardData');
    localStorage.removeItem('cardId');
    localStorage.removeItem('contactLinkUUID');
    localStorage.removeItem('tempLinksFetched');
    sessionStorage.clear();
  };

  // Simplified error handling - no special cases for signup
  const handleAuthError = (error) => {
    console.error('Auth error:', error);

    // Add error to global error context for consistent display
    addError(error, {
      source: 'auth',
      type: 'auth',
      retryable: false,
      autoDismiss: false
    });

    if (error.response?.status === 401) {
      console.log('Session expired, logging out user');
      clearAuthState();
      setAuthError('Your session has expired. Please log in again.');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 1000);
      return;
    }

    if (error.response?.status === 403) {
      console.error('Access denied:', error.message);
      setAuthError('You do not have permission to perform this action.');
      return;
    }

    if (error.message?.includes('permission')) {
      setAuthError('You do not have permission to perform this action.');
      return;
    }

    if (error.message?.includes('session')) {
      clearAuthState();
      setAuthError('Your session has expired. Please log in again.');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 1000);
      return;
    }

    // Generic error handling
    setAuthError(error.message || 'An error occurred. Please try again.');
  };

  // Simplified zombie user handling - no signup flow detection
  const handleZombieUser = async (firebaseUser, idToken) => {
    console.log('🧟 Detected zombie user:', firebaseUser.uid);

    // Store zombie user info for potential recovery
    setZombieUser({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified,
      displayName: firebaseUser.displayName,
      idToken: idToken
    });

    // Always set as not authenticated for zombie users
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null
    });

    // Set a helpful error message
    setAuthError('Your account setup is incomplete. Please complete your registration or sign in again.');
  };

  // Recovery function for zombie users
  const recoverZombieUser = async () => {
    if (!zombieUser) return null;

    try {
      console.log('🔄 Attempting zombie user recovery');

      // Import the registerUser function
      const { registerUser } = await import('../apis/authApi');

      // Create the user in MongoDB using the registration endpoint
      const recoveryData = {
        userEmail: zombieUser.email,
        fullName: zombieUser.displayName || zombieUser.email.split('@')[0],
        userId: zombieUser.uid
      };

      console.log('🔄 Creating user in MongoDB:', recoveryData);

      // Call the backend to create the user
      const response = await handleApiCall(
        () => registerUser(recoveryData.userEmail, 'tempPassword123', recoveryData.fullName),
        {
          source: 'zombie-user-recovery',
          retryable: true,
          autoDismiss: false
        }
      );

      if (response.success) {
        console.log('✅ Zombie user recovered successfully');
        setZombieUser(null);
        return response.user;
      } else {
        console.error('❌ Failed to recover zombie user:', response.message);
        return null;
      }
    } catch (error) {
      console.error('Failed to recover zombie user:', error);
      return null;
    }
  };

  // Clear zombie user state
  const clearZombieUser = async () => {
    if (zombieUser) {
      try {
        // Sign out the zombie user from Firebase
        await auth.signOut();
        setZombieUser(null);
        clearAuthState();
        console.log('🧟 Zombie user cleared');
      } catch (error) {
        console.error('Error clearing zombie user:', error);
      }
    }
  };

  // Clear error after a timeout
  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        setAuthError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [authError]);

  // Helper function to update auth state and localStorage
  const updateAuthState = (newState) => {
    setAuthState(newState);
    setAuthError(null);
    setZombieUser(null);

    if (newState.isAuthenticated && newState.token && newState.user) {
      localStorage.setItem('authToken', newState.token);
      localStorage.setItem('authUser', JSON.stringify(newState.user));
      console.log('🔌 AuthContext: Authentication successful, userId:', newState.user.userId);
    } else {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      console.log('🔌 AuthContext: Authentication cleared');
    }
  };

  // Simplified Firebase auth state observer - no special signup handling
  useEffect(() => {
    // Add timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout - forcing loading to false');
      setLoading(false);
    }, 3000);

    let isProcessing = false;

    // Check if Firebase is available
    if (!auth) {
      console.warn('Firebase auth not available, setting loading to false');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (isProcessing) return;
      isProcessing = true;

      try {
        clearTimeout(loadingTimeout);

        if (user) {
          const idToken = await user.getIdToken();

          // Always try to fetch complete user data - no special cases
          try {
            const userData = await handleApiCall(
              () => getUserById(user.uid, idToken),
              {
                source: 'auth-state-change',
                retryable: true,
                autoDismiss: true
              }
            );

            // Get the active card ID from the user's cards
            const activeCard = userData.data.contactCardIds?.find(card => card.isActive) || userData.data.contactCardIds?.[0];

            updateAuthState({
              isAuthenticated: true,
              token: idToken,
              user: {
                ...userData.data,
                userId: user.uid, // ALWAYS use Firebase UID as userId
                uid: user.uid,
                email: user.email,
                emailVerified: user.emailVerified,
                activeCardId: activeCard?._id
              }
            });
          } catch (error) {
            if (error.response?.status === 404) {
              // Handle zombie user
              await handleZombieUser(user, idToken);
            } else {
              console.error('Error fetching user data:', error);
              // Even if getUserById fails, we should still set the user as authenticated
              // with basic Firebase user data, so the app can continue
              updateAuthState({
                isAuthenticated: true,
                token: idToken,
                user: {
                  userId: user.uid,
                  uid: user.uid,
                  email: user.email,
                  emailVerified: user.emailVerified
                }
              });
              handleAuthError(error);
            }
          }
        } else {
          updateAuthState({
            isAuthenticated: false,
            token: null,
            user: null
          });
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        handleAuthError(error);
      } finally {
        setLoading(false);
        isProcessing = false;
      }
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
      setLoading(false);
    };
  }, [handleApiCall, addError]);

  const login = async (userEmail, userPassword) => {
    try {
      setAuthError(null);

      const response = await handleApiCall(
        () => loginUser(userEmail, userPassword),
        {
          source: 'login',
          retryable: true,
          autoDismiss: false
        }
      );

      if (response.success) {
        if (!response.emailVerified) {
          await auth.signOut();
          return {
            success: false,
            code: 'auth/email-not-verified',
            message: 'Please verify your email before logging in'
          };
        }

        // Get current Firebase user to get the UID
        const firebaseUser = auth.currentUser;
        const firebaseUid = firebaseUser?.uid;

        updateAuthState({
          isAuthenticated: true,
          token: response.idToken,
          user: {
            ...response.user,
            userId: firebaseUid || response.user.userId, // ALWAYS use Firebase UID as userId
            uid: firebaseUid || response.user.userId
          }
        });
        
        // Determine redirect path based on user plan and role
        const redirectPath = await getLoginRedirectPath(response.user, response.idToken);
        window.location.href = redirectPath;
        return response;
      }
      return response;

    } catch (error) {
      console.error('Login error:', error);
      handleAuthError(error);
      throw error;
    }
  };

  // Simplified Google Sign In - no special signup handling
  const handleGoogleSignIn = async () => {
    try {
      setAuthError(null);

      const response = await handleApiCall(
        () => googleSignIn(),
        {
          source: 'google-signin',
          retryable: true,
          autoDismiss: false
        }
      );

      if (response.success) {
        updateAuthState({
          isAuthenticated: true,
          token: response.idToken,
          user: {
            ...response.user,
            userId: response.firebaseUser.uid, // ALWAYS use Firebase UID as userId
            uid: response.firebaseUser.uid,
            email: response.firebaseUser.email,
            emailVerified: response.firebaseUser.emailVerified
          }
        });
        return response;
      } else {
        if (response.message) {
          setAuthError(response.message);
        }
        return response;
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      handleAuthError(error);
      return {
        success: false,
        message: error.message || 'An error occurred during Google sign-in'
      };
    }
  };

  // Simplified Google Sign Up - no special signup flow handling
  const handleGoogleSignUp = async () => {
    try {
      setAuthError(null);

      const response = await handleApiCall(
        () => googleSignUp(),
        {
          source: 'google-signup',
          retryable: true,
          autoDismiss: false
        }
      );

      if (response.success) {
        updateAuthState({
          isAuthenticated: true,
          token: response.idToken,
          user: {
            ...response.user,
            userId: response.firebaseUser.uid, // ALWAYS use Firebase UID as userId
            uid: response.firebaseUser.uid,
            email: response.firebaseUser.email,
            emailVerified: response.firebaseUser.emailVerified
          }
        });
        return response;
      } else {
        if (response.message) {
          setAuthError(response.message);
        }
        throw new Error(response.message || 'Google sign-up failed');
      }
    } catch (error) {
      console.error('Google sign-up error:', error);
      handleAuthError(error);
      throw error;
    }
  };

  const facebookSignIn = async () => {
    try {
      setAuthError(null);

      const response = await handleApiCall(
        () => facebookLogin(),
        {
          source: 'facebook-signin',
          retryable: true,
          autoDismiss: false
        }
      );

      if (response.success) {
        // Get current Firebase user to get the UID
        const firebaseUser = auth.currentUser;
        const firebaseUid = firebaseUser?.uid;

        updateAuthState({
          isAuthenticated: true,
          token: response.customToken,
          user: {
            ...response.user,
            userId: firebaseUid || response.user.userId, // ALWAYS use Firebase UID as userId
            uid: firebaseUid || response.user.userId
          }
        });
      }
      return response;
    } catch (error) {
      console.error('Facebook sign-in error:', error);
      handleAuthError(error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      logoutUser();
      clearAuthState();
      setAuthError(null);
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthState();
      setAuthError(null);
    }
  };

  const isAuthenticated = () => {
    return authState.isAuthenticated &&
      authState.user?.emailVerified &&
      authState.token !== null;
  };

  const refreshToken = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const newToken = await user.getIdToken(true);
        updateAuthState({
          ...authState,
          token: newToken
        });
        return newToken;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      handleAuthError(error);
      throw error;
    }
  };

  // Simplified validation functions - no special signup cases
  const validateUserAccess = (resourceUserId, currentUserId = authState.user?.userId) => {
    if (!currentUserId) {
      throw new Error('Authentication required');
    }

    if (resourceUserId !== currentUserId) {
      throw new Error('You do not have permission to access this resource');
    }

    return true;
  };

  const isOwner = (resourceUserId, currentUserId = authState.user?.userId) => {
    return resourceUserId === currentUserId;
  };

  const handleUnauthorizedAccess = () => {
    console.error('Unauthorized access attempt');
    setAuthError('You do not have permission to access this resource');
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 2000);
  };

  // Always return Firebase UID as userId
  const getCurrentUserId = () => {
    const userId = authState.user?.userId || authState.user?.uid;
    return userId;
  };

  // Function to fetch user data after signup completes
  const fetchUserDataAfterSignup = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return;
      }

      const idToken = await user.getIdToken(true);

      const userData = await handleApiCall(
        () => getUserById(user.uid, idToken),
        {
          source: 'post-signup-fetch',
          retryable: true,
          autoDismiss: true
        }
      );

      // Get the active card ID from the user's cards
      const activeCard = userData.data.contactCardIds?.find(card => card.isActive) || userData.data.contactCardIds?.[0];

      updateAuthState({
        isAuthenticated: true,
        token: idToken,
        user: {
          ...userData.data,
          userId: user.uid, // ALWAYS use Firebase UID as userId
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          activeCardId: activeCard?._id
        }
      });
    } catch (error) {
      console.error('🔍 AuthContext: Error fetching user data after signup:', error);
      // Don't throw - let the signup flow continue
    }
  };

  const hasPermission = (permission) => {
    return authState.user?.permissions?.includes(permission) || false;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const value = {
    authState,
    authError,
    zombieUser,
    login,
    googleSignIn: handleGoogleSignIn,
    googleSignUp: handleGoogleSignUp,
    facebookSignIn,
    logout,
    isAuthenticated,
    refreshToken,
    handleAuthError,
    validateUserAccess,
    isOwner,
    handleUnauthorizedAccess,
    getCurrentUserId,
    hasPermission,
    clearAuthError: () => setAuthError(null),
    recoverZombieUser,
    clearZombieUser,
    fetchUserDataAfterSignup
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};